import { useQuery } from "@tanstack/react-query";
import { useAnalytics } from "../../../../contexts/analytics/hooks";
import { useChosenDepartment } from "../../../../contexts/ChosenDepartment";
import { backendService } from "../../../../services/backend.service";
import type { HiringEligibleStaffResponse, HiringRecommendation } from "../types";

export const HIRING_ELIGIBLE_STAFF_QUERY_KEY = "hiring-eligible-staff";

/** How long a card's eligible-staff list stays fresh — keyed to how often a schedule template changes. */
const ELIGIBLE_STAFF_STALE_MS = 5 * 60 * 1000;

type Args = {
	rec: HiringRecommendation;
	enabled: boolean;
};

export function useHiringEligibleStaff({ rec, enabled }: Args) {
	const { chosenDepartment } = useChosenDepartment();
	const { track, trackError } = useAnalytics();
	const fromSlot = rec.fromSlot;
	const toSlot = rec.toSlot;

	return useQuery<HiringEligibleStaffResponse>({
		queryKey: [HIRING_ELIGIBLE_STAFF_QUERY_KEY, chosenDepartment.department_id, rec.jobId, fromSlot?.dayIdx, toSlot?.dayIdx, fromSlot?.shiftLabel],
		queryFn: async () => {
			if (!fromSlot || !toSlot) {
				throw new Error("missing_slots");
			}
			track("hiring_eligible_staff_fetch_start", { department_id: chosenDepartment.department_id, job_id: rec.jobId });
			try {
				const { data } = await backendService.getHiringEligibleStaff(chosenDepartment.department_id, {
					fromDay: fromSlot.dayIdx,
					toDay: toSlot.dayIdx,
					fromShift: fromSlot.shiftLabel,
					jobId: rec.jobId,
				});
				track("hiring_eligible_staff_fetch_success", { count: data.staff.length });
				return data;
			} catch (err) {
				const message = err instanceof Error ? err.message : "unknown";
				trackError("hiring_eligible_staff_fetch_error", { error_message: message });
				throw err;
			}
		},
		enabled: enabled && Boolean(fromSlot && toSlot),
		/**
		 * One query per Move CARD, and the board mounts eight at a time (`CARD_BATCH`), with every "Show more"
		 * adding eight that stay mounted. This hook used to sit behind a modal (`enabled: open`), so one click
		 * meant one request; on the card face it has to load without one.
		 *
		 * Which makes the defaults wrong here. `staleTime: 0` plus TanStack v5's `refetchOnWindowFocus` re-ran
		 * every mounted card's query on each tab focus, and each one costs a full-department read
		 * (`get_active_recurring_shifts_with_job_for_dept` has no day/shift predicate). Who can cover a
		 * recurring slot only changes when the schedule template does, which is rarely — and a save through
		 * the recurring-shift panel invalidates the page's payload anyway.
		 */
		staleTime: ELIGIBLE_STAFF_STALE_MS,
		refetchOnWindowFocus: false,
	});
}
